import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import './index.css';
import App from './App';
import { readAuthConfig, AuthConfigError } from './auth/config';
import { initAuth, getMsalInstance, loginRequestFor } from './auth';

const root = ReactDOM.createRoot(document.getElementById('root'));

// Plain, dependency-free fatal screen. Rendered when auth cannot be configured;
// we must never fall through to an unauthenticated app.
function renderFatalError(detail) {
  root.render(
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif',
        background: '#f7f7f8',
        padding: '2rem',
      }}
    >
      <div
        style={{
          maxWidth: 520,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#b91c1c' }}>
          Application cannot start
        </h1>
        <p style={{ margin: '0 0 1rem', color: '#374151', lineHeight: 1.5 }}>
          The authentication configuration is missing or invalid, so the app was
          not started. This is a deployment/configuration problem — please
          contact an administrator.
        </p>
        <pre
          style={{
            margin: 0,
            padding: '0.75rem',
            background: '#f3f4f6',
            borderRadius: 8,
            color: '#374151',
            fontSize: '0.8rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {detail}
        </pre>
      </div>
    </div>
  );
}

async function bootstrap() {
  // 1. Strictly validate runtime config — fail closed on any problem.
  let cfg;
  try {
    cfg = readAuthConfig();
  } catch (err) {
    renderFatalError(err instanceof AuthConfigError ? err.message : String(err));
    return;
  }

  // 2. Initialize MSAL.
  try {
    await initAuth(
      cfg.clientId,
      cfg.authority,
      cfg.knownAuthorities,
      cfg.cacheLocation,
      cfg.resources
    );
  } catch (err) {
    renderFatalError(`Failed to initialize authentication: ${(err && err.message) || err}`);
    return;
  }

  // 3. Render inside the MSAL provider, forcing sign-in before the app shows.
  root.render(
    <React.StrictMode>
      <MsalProvider instance={getMsalInstance()}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={loginRequestFor('api')}
        >
          <App />
        </MsalAuthenticationTemplate>
      </MsalProvider>
    </React.StrictMode>
  );
}

bootstrap();
